/**
 * Bundle acquisition — the ONLY module in the load path that performs I/O
 * (architecture.md §6, step 1).
 *
 * @remarks
 * A `GameBundleSource` is an adapter around "where the bytes come from":
 * local JSON import today (Phases 0–4), static fetch and CDN/preview
 * endpoints later. The loader never knows which it got — it calls `acquire`
 * once and treats everything after as untrusted `unknown` until validated.
 *
 * @packageDocumentation
 */

/** One place a bundle document can come from. */
export interface GameBundleSource {
	/** Produce the raw, untrusted bundle document. Rejects/throws → `source_unreachable`. */
	readonly acquire: () => Promise<unknown>;
	/**
	 * R24 (D93): where this source's pack-relative asset paths resolve — a
	 * plate's `src` is relative to the PACK, and only the source knows where
	 * the pack's files are actually served from. Optional because a pack with
	 * no assets needs no base; carried onto the `runtimeManifest` so shells
	 * resolve without knowing which source kind produced the bundle. A remote
	 * source will derive its own from its endpoint; a local one declares it.
	 */
	readonly assetBaseUrl?: string;
	/** Human-readable identity for failure messages (e.g. `local JSON import: orpheus-protocol`). */
	readonly description: string;
}

/** Options for {@link createLocalJsonSource}. */
export interface CreateLocalJsonSourceOptions {
	/** Where this pack's asset paths resolve (see {@link GameBundleSource.assetBaseUrl}). */
	readonly assetBaseUrl?: string;
}

/**
 * The v0 adapter: a document the caller already has in memory (a static
 * `import`, an inlined fixture, a test literal) behind the async source
 * contract.
 *
 * @param bundleDocument - The raw document (untrusted until validated)
 * @param description - Source identity for failure messages
 * @param options - Source extras (the asset base URL, when the app serves the pack's files)
 * @returns The source adapter
 */
export const createLocalJsonSource = (
	bundleDocument: unknown,
	description: string = 'local JSON import',
	options: CreateLocalJsonSourceOptions = {},
): GameBundleSource => ({
	acquire: async () => Promise.resolve(bundleDocument),
	...(options.assetBaseUrl === undefined ? {} : { assetBaseUrl: options.assetBaseUrl }),
	description,
});
